import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult, DraggableProvided, DraggableStateSnapshot, DroppableProvided, DroppableStateSnapshot } from 'react-beautiful-dnd';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { Calendar as CalendarIcon, Plus, Clock, AlertCircle, CheckCircle, ArrowUpDown, Trash2, Edit, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import TaskService, { Task } from '@/services/TaskService';
import { useAuth } from '@/contexts/AuthContext';
import { isUserAdmin } from '@/utils/adminHelper';

const PRIORITY_COLORS = {
  low: 'bg-green-100 text-green-800',
  medium: 'bg-yellow-100 text-yellow-800',
  high: 'bg-red-100 text-red-800'
};

type TaskStatus = 'todo' | 'in-progress' | 'review' | 'done';

const STATUS_STYLES: Record<TaskStatus, {
  bg: string;
  border: string;
  headerBg: string;
  title: string;
}> = {
  'todo': {
    bg: 'bg-slate-50',
    border: 'border-slate-200',
    headerBg: 'bg-slate-100/50',
    title: 'Agenda and Goals'
  },
  'in-progress': {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    headerBg: 'bg-blue-100/50',
    title: 'In Progress'
  },
  'review': {
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    headerBg: 'bg-purple-100/50',
    title: 'Review'
  },
  'done': {
    bg: 'bg-green-50',
    border: 'border-green-200',
    headerBg: 'bg-green-100/50',
    title: 'Done'
  }
};

const COLUMNS: { id: TaskStatus; title: string }[] = [
  { id: 'todo', title: 'Agenda and Goals' },
  { id: 'in-progress', title: 'In Progress' },
  { id: 'review', title: 'Review' },
  { id: 'done', title: 'Done' }
];

export const KanbanBoard = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [isAddTaskOpen, setIsAddTaskOpen] = useState(false);
  const [isEditTaskOpen, setIsEditTaskOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [newTask, setNewTask] = useState<Partial<Task>>({
    title: '',
    description: '',
    priority: 'medium',
    due_date: null,
    assignee: '',
    status: 'todo'
  });
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (user) {
        const adminStatus = await isUserAdmin();
        setIsAdmin(adminStatus);
      }
    };
    
    checkAdminStatus();
    fetchTasks();
  }, [user]);

  const fetchTasks = async () => {
    setIsLoading(true);
    const tasksData = await TaskService.getTasks();
    setTasks(tasksData);
    setIsLoading(false);
  };

  const handleDragEnd = async (result: DropResult) => {
    const { destination, source, draggableId } = result;

    if (!destination) return;

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const newStatus = destination.droppableId as TaskStatus;
    
    const taskToMove = tasks.find(t => t.id === draggableId);
    if (!taskToMove) return;

    const newTasks = [...tasks];
    
    const sourceItems = newTasks.filter(t => t.status === source.droppableId);
    const otherItems = newTasks.filter(t => t.status !== source.droppableId);
    sourceItems.splice(source.index, 1);
    
    const destinationItems = newTasks.filter(t => t.status === destination.droppableId);
    const updatedTask = { ...taskToMove, status: newStatus };
    destinationItems.splice(destination.index, 0, updatedTask);

    const finalTasks = [
      ...otherItems,
      ...sourceItems,
      ...destinationItems
    ].filter(Boolean);

    setTasks(finalTasks);

    try {
      const success = await TaskService.updateTaskStatus(draggableId, newStatus);
      if (!success) {
        setTasks(tasks);
        toast.error('Failed to update task status');
      }
    } catch (error) {
      setTasks(tasks);
      toast.error('Failed to update task status');
    }
  };

  const handleAddTask = async () => {
    if (!newTask.title) {
      toast.error('Please enter a task title');
      return;
    }

    if (!user) {
      toast.error('You must be logged in to create tasks');
      return;
    }

    const task = {
      ...newTask,
      created_by: user.id
    } as Omit<Task, 'id' | 'created_at' | 'updated_at'>;

    const createdTask = await TaskService.createTask(task);
    if (createdTask) {
      fetchTasks();
      setIsAddTaskOpen(false);
      setNewTask({
        title: '',
        description: '',
        priority: 'medium',
        due_date: null,
        assignee: '',
        status: 'todo'
      });
    }
  };

  const handleEditTask = async () => {
    if (!selectedTask || !selectedTask.id) return;

    const updatedTask = await TaskService.updateTask(selectedTask.id, {
      title: selectedTask.title,
      description: selectedTask.description,
      priority: selectedTask.priority,
      due_date: selectedTask.due_date,
      assignee: selectedTask.assignee
    });

    if (updatedTask) {
      fetchTasks();
      setIsEditTaskOpen(false);
      setSelectedTask(null);
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      const success = await TaskService.deleteTask(taskId);
      if (success) {
        fetchTasks();
      }
    }
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-[400px]">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium">Access Restricted</h3>
          <p className="text-gray-500 mt-2">Only administrators can access the task management system.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col space-y-8 p-8">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-gray-800">Team Brainstorm</h2>
          <p className="text-sm text-gray-500">Manage and track tasks across the platform</p>
        </div>
        <Button 
          onClick={() => setIsAddTaskOpen(true)} 
          size="sm"
          className="bg-[#4573d2] hover:bg-[#3d68c1] text-white"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Task
        </Button>
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="grid grid-cols-4 gap-4">
          {COLUMNS.map((column) => (
            <div key={column.id} className="flex flex-col">
              <div className={cn(
                "px-4 py-2 rounded-t-lg",
                STATUS_STYLES[column.id].headerBg
              )}>
                <h3 className="font-medium text-sm text-gray-700">
                  {column.title}
                </h3>
              </div>
              <Droppable droppableId={column.id}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={cn(
                      "flex flex-col gap-2 p-2 rounded-b-lg min-h-[700px] transition-colors duration-200",
                      STATUS_STYLES[column.id].bg,
                      STATUS_STYLES[column.id].border,
                      snapshot.isDraggingOver && "ring-2 ring-[#4573d2] ring-opacity-50"
                    )}
                  >
                    {tasks
                      .filter(task => task.status === column.id)
                      .map((task, index) => (
                        <Draggable
                          key={task.id}
                          draggableId={task.id}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              style={provided.draggableProps.style}
                              className={cn(
                                "group bg-white rounded-lg border shadow-sm hover:shadow-md transition-all duration-200",
                                snapshot.isDragging && "shadow-lg ring-2 ring-[#4573d2] rotate-2 z-50",
                                "cursor-grab active:cursor-grabbing"
                              )}
                            >
                              <div className="p-3">
                                <div className="space-y-2">
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1 min-w-0">
                                      <h4 className="font-medium text-gray-900 truncate">
                                        {task.title}
                                      </h4>
                                      {task.description && (
                                        <p className="text-sm text-gray-500 line-clamp-2 mt-1">
                                          {task.description}
                                        </p>
                                      )}
                                    </div>
                                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-7 w-7 text-gray-500 hover:text-gray-700"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedTask(task);
                                          setIsEditTaskOpen(true);
                                        }}
                                      >
                                        <Edit className="h-3.5 w-3.5" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-7 w-7 text-gray-500 hover:text-red-600"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteTask(task.id);
                                        }}
                                      >
                                        <Trash2 className="h-3.5 w-3.5" />
                                      </Button>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <Badge 
                                      variant="secondary" 
                                      className={cn(
                                        "text-xs font-normal",
                                        PRIORITY_COLORS[task.priority]
                                      )}
                                    >
                                      {task.priority}
                                    </Badge>
                                    {task.due_date && (
                                      <Badge 
                                        variant="outline" 
                                        className="text-xs font-normal text-gray-600 flex items-center gap-1"
                                      >
                                        <Clock className="h-3 w-3" />
                                        {format(new Date(task.due_date), 'MMM dd')}
                                      </Badge>
                                    )}
                                    {task.assignee && (
                                      <Badge 
                                        variant="outline" 
                                        className="text-xs font-normal text-gray-600"
                                      >
                                        {task.assignee}
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          ))}
        </div>
      </DragDropContext>

      <Dialog open={isAddTaskOpen} onOpenChange={setIsAddTaskOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Add New Task</DialogTitle>
            <DialogDescription>
              Create a new task to add to your Kanban board.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Title</label>
              <Input
                value={newTask.title}
                onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                placeholder="Enter task title"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Description</label>
              <Input
                value={newTask.description}
                onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                placeholder="Enter task description"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Priority</label>
              <Select
                value={newTask.priority}
                onValueChange={(value) => setNewTask({ ...newTask, priority: value as 'low' | 'medium' | 'high' })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Due Date</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !date && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {date ? format(date, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={(date) => {
                      setDate(date);
                      setNewTask({ ...newTask, due_date: date });
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Assignee</label>
              <Input
                value={newTask.assignee}
                onChange={(e) => setNewTask({ ...newTask, assignee: e.target.value })}
                placeholder="Enter assignee name"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddTaskOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddTask}>Add Task</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isEditTaskOpen} onOpenChange={setIsEditTaskOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Edit Task</DialogTitle>
            <DialogDescription>
              Modify the details of your existing task.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Title</label>
              <Input
                value={selectedTask?.title}
                onChange={(e) => setSelectedTask(prev => prev ? { ...prev, title: e.target.value } : null)}
                placeholder="Enter task title"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Description</label>
              <Input
                value={selectedTask?.description}
                onChange={(e) => setSelectedTask(prev => prev ? { ...prev, description: e.target.value } : null)}
                placeholder="Enter task description"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Priority</label>
              <Select
                value={selectedTask?.priority}
                onValueChange={(value) => setSelectedTask(prev => prev ? { ...prev, priority: value as 'low' | 'medium' | 'high' } : null)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Due Date</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !selectedTask?.due_date && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {selectedTask?.due_date ? format(new Date(selectedTask.due_date), "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={selectedTask?.due_date ? new Date(selectedTask.due_date) : undefined}
                    onSelect={(date) => setSelectedTask(prev => prev ? { ...prev, due_date: date } : null)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Assignee</label>
              <Input
                value={selectedTask?.assignee}
                onChange={(e) => setSelectedTask(prev => prev ? { ...prev, assignee: e.target.value } : null)}
                placeholder="Enter assignee name"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditTaskOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditTask}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}; 